import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterPhraseOption } from './FilterPhraseOption';
import { HtmlSelectorOption } from './HtmlSelectorOption';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';

type AdvancedOptionsData = {
  htmlSelector: string;
  useHtmlSelector: boolean;
  filterPhrase: string;
  useFilterPhrase: boolean;
};

type Props = {
  type: WebsiteModalTypeEnum;
  onChange?: (data: AdvancedOptionsData) => void;
  reset?: boolean;
  onResetComplete?: () => void;
  error?: boolean;
};

export const AdvancedOptions = ({ type, onChange, reset, onResetComplete, error }: Props) => {
  const { t } = useTranslation(['knowledge_base']);
  const [isResetting, setIsResetting] = useState(false);
  const [htmlSelectorData, setHtmlSelectorData] = useState<{
    useHtmlSelector: boolean;
    htmlSelector: string;
  }>({
    useHtmlSelector: false,
    htmlSelector: '',
  });
  const [filterPhraseData, setFilterPhraseData] = useState<{
    useFilterPhrase: boolean;
    filterPhrase: string;
  }>({
    useFilterPhrase: false,
    filterPhrase: '',
  });

  useEffect(() => {
    if (reset && !isResetting) {
      setIsResetting(true);
    }
  }, [reset, isResetting]);

  const handleResetComplete = () => {
    if (isResetting) {
      setIsResetting(false);
      if (onResetComplete) {
        onResetComplete();
      }
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange({
        ...htmlSelectorData,
        ...filterPhraseData,
      });
    }
  }, [htmlSelectorData, filterPhraseData, onChange]);

  return (
    <div className="pt-4" data-testid="upload-url-advanced-options">
      <h3 className="font-medium text-sm mb-4">{t('modals.upload_url.advanced_options')}</h3>

      <div className="space-y-4">
        <HtmlSelectorOption
          onChange={(data) =>
            setHtmlSelectorData({
              useHtmlSelector: data.useHtmlSelector,
              htmlSelector: data.selector,
            })
          }
          reset={isResetting}
          onResetComplete={handleResetComplete}
          error={error}
        />

        {type === WebsiteModalTypeEnum.SITEMAP && (
          <FilterPhraseOption
            onChange={setFilterPhraseData}
            reset={isResetting}
            onResetComplete={handleResetComplete}
            error={error}
          />
        )}
      </div>
    </div>
  );
};
